import { observableValue, derived, IReader } from '@vscode/observables';
import { ViewModel, prop } from '@vscode/observables-react';
import type { FixtureRegistry, ComponentDefinition } from '../core/index.js';
import { FixturePath } from '../core/FixturePath.js';
import { type ExplorerTreeNode, toExplorerTree } from './types.js';
import { filterTree } from './treeFilter.js';
import { createLocalStorageObservable } from '../lib/localStorageObservable.js';
import { createQueryParamObservable } from '../lib/queryParamObservable.js';
import type { DaemonModel } from '../daemon/index.js';
import type { ReportModel } from '../report/ReportModel.js';
import type { ReportFixtureItem } from './ReportComparisonRow.js';

export type ViewMode = 'session' | 'diff' | 'report';

/**
 * A fixture item to be rendered in the preview area.
 */
export interface FixtureItem {
  readonly id: string;
  readonly name: string;
  readonly path: string;
  readonly component: ComponentDefinition;
}

/**
 * A screenshot comparison item for diff mode.
 */
export interface ScreenshotComparisonItem {
  readonly fixtureId: string;
  readonly name: string;
  readonly path: string;
}

/**
 * A preview row can be a live fixture, a variant group, or a screenshot comparison.
 */
export type PreviewRow =
  | { readonly type: 'single'; readonly fixture: FixtureItem }
  | { readonly type: 'variants'; readonly id: string; readonly name: string; readonly path: string; readonly fixtures: readonly FixtureItem[] }
  | { readonly type: 'screenshot-comparison'; readonly id: string; readonly name: string; readonly path: string; readonly items: readonly ScreenshotComparisonItem[] }
  | { readonly type: 'report-comparison'; readonly id: string; readonly name: string; readonly path: string; readonly items: readonly ReportFixtureItem[] };

export class ExplorerModel extends ViewModel({
  registry: prop.const<FixtureRegistry>(),
  daemonModel: prop.const<DaemonModel | undefined>(),
  reportModel: prop.const<ReportModel | undefined>(),
}) {
  readonly leftSidebarVisible = createLocalStorageObservable<boolean>(this, {
    storageKey: 'component-explorer:leftSidebarVisible',
    defaultValue: true,
  });
  readonly rightSidebarVisible = createLocalStorageObservable<boolean>(this, {
    storageKey: 'component-explorer:rightSidebarVisible',
    defaultValue: false,
  });
  readonly isDarkTheme = createLocalStorageObservable<boolean>(this, {
    storageKey: 'component-explorer:isDarkTheme',
    defaultValue: true,
  });
  readonly selectedNodeId = createQueryParamObservable(this, 'fixture');
  readonly expandedNodeIds = observableValue<ReadonlySet<string>>(this, this._initialExpandedNodes());

  readonly viewMode = createLocalStorageObservable<ViewMode>(this, {
    storageKey: 'component-explorer:viewMode',
    defaultValue: 'session',
  });

  get daemonAvailable(): boolean { return this.props.daemonModel !== undefined; }
  get daemon(): DaemonModel | undefined { return this.props.daemonModel; }
  get reportModel(): ReportModel | undefined { return this.props.reportModel; }
  get isReportMode(): boolean { return this.props.reportModel !== undefined; }

  readonly loading = derived(this, (reader) => this.props.registry.loading.read(reader));

  readonly searchQuery = createQueryParamObservable(this, 'search');

  /** The explorer's tree derived from the registry or report */
  readonly tree = derived(this, (reader) => {
    const report = this.props.reportModel;
    if (report) {
      return report.buildTree(reader);
    }
    const root = this.props.registry.root.read(reader);
    return toExplorerTree(root);
  });

  readonly filteredTree = derived(this, (reader) => {
    const tree = this.tree.read(reader);
    const query = this.searchQuery.read(reader);
    return filterTree(tree, query) ?? { ...tree, children: [] };
  });

  readonly selectedNode = derived(this, (reader) => {
    const nodeId = this.selectedNodeId.read(reader);
    if (nodeId === undefined) return undefined;
    const tree = this.filteredTree.read(reader);
    if (tree.id.value === nodeId) return tree;
    return this._findNode(tree.children ?? [], nodeId);
  });

  /** List of preview rows to render based on current selection and view mode */
  readonly selectedFixtures = derived(this, (reader): readonly PreviewRow[] => {
    const selectedNode = this.selectedNode.read(reader);
    if (!selectedNode) return [];

    const pathPrefix = selectedNode.type === 'component'
      ? selectedNode.id.segments.slice(-3).join(' / ')
      : selectedNode.name;

    if (this.isReportMode) {
      const report = this.props.reportModel!;
      report.state.read(reader);
      return this._collectReportRows(selectedNode, pathPrefix);
    }

    const mode = this.viewMode.read(reader);
    if (mode === 'diff' && this.daemonAvailable) {
      const sessions = this.daemon!.sessions.read(reader);
      if (sessions.length > 1) {
        return this._collectDiffRows(selectedNode, pathPrefix);
      }
    }

    return this._collectPreviewRows(selectedNode, pathPrefix);
  });

  setViewMode = (mode: ViewMode): void => {
    this.viewMode.set(mode, undefined);
  };

  navigateToSession = (sessionName: string): void => {
    const daemon = this.props.daemonModel;
    if (!daemon) return;
    const url = daemon.getSessionExplorerUrl(sessionName);
    if (url) {
      window.location.href = url;
    }
  };

  toggleLeftSidebar = (): void => {
    this.leftSidebarVisible.set(!this.leftSidebarVisible.get(), undefined);
  };

  toggleRightSidebar = (): void => {
    this.rightSidebarVisible.set(!this.rightSidebarVisible.get(), undefined);
  };

  toggleTheme = (): void => {
    this.isDarkTheme.set(!this.isDarkTheme.get(), undefined);
  };

  selectNode = (nodeId: string): void => {
    this.selectedNodeId.set(nodeId, undefined);
  };

  toggleNodeExpanded = (nodeId: string): void => {
    const current = this.expandedNodeIds.get();
    const newSet = new Set(current);
    if (newSet.has(nodeId)) {
      newSet.delete(nodeId);
    } else {
      newSet.add(nodeId);
    }
    this.expandedNodeIds.set(newSet, undefined);
  };

  isNodeExpanded(nodeId: string, reader: IReader): boolean {
    if (this.searchQuery.read(reader)) return true;
    return this.expandedNodeIds.read(reader).has(nodeId);
  }

  private _initialExpandedNodes(): ReadonlySet<string> {
    const selected = this.selectedNodeId.get();
    if (!selected) return new Set(['']);
    const parts = new FixturePath(selected).segments;
    const expanded = new Set(['']);
    for (let i = 1; i <= parts.length; i++) {
      expanded.add(parts.slice(0, i).join('/'));
    }
    return expanded;
  }

  private _findNode(nodes: readonly ExplorerTreeNode[], id: string): ExplorerTreeNode | undefined {
    for (const node of nodes) {
      if (node.id.value === id) return node;
      if (node.children) {
        const found = this._findNode(node.children, id);
        if (found) return found;
      }
    }
    return undefined;
  }

  private _collectPreviewRows(node: ExplorerTreeNode, pathPrefix: string): PreviewRow[] {
    if (node.type === 'component' && node.component) {
      return [{
        type: 'single',
        fixture: {
          id: node.id.value,
          name: node.name,
          path: pathPrefix,
          component: node.component,
        },
      }];
    }

    if (node.type === 'variants') {
      const fixtures: FixtureItem[] = [];
      for (const child of node.children ?? []) {
        if (child.type === 'component' && child.component) {
          fixtures.push({
            id: child.id.value,
            name: child.name,
            path: `${pathPrefix} / ${child.name}`,
            component: child.component,
          });
        }
      }
      if (fixtures.length > 0) {
        return [{
          type: 'variants',
          id: node.id.value,
          name: node.name,
          path: pathPrefix,
          fixtures,
        }];
      }
      return [];
    }

    // Folder: recurse into children
    const result: PreviewRow[] = [];
    for (const child of node.children ?? []) {
      const childPath = pathPrefix ? `${pathPrefix} / ${child.name}` : child.name;
      result.push(...this._collectPreviewRows(child, childPath));
    }
    return result;
  }

  /**
   * In diff mode: fixtures become screenshot comparison items showing current vs baseline.
   * Variants become comparison groups.
   */
  private _collectDiffRows(node: ExplorerTreeNode, pathPrefix: string): PreviewRow[] {
    if (node.type === 'component') {
      return [{
        type: 'screenshot-comparison',
        id: node.id.value,
        name: node.name,
        path: pathPrefix,
        items: [{
          fixtureId: node.id.value,
          name: node.name,
          path: pathPrefix,
        }],
      }];
    }

    if (node.type === 'variants') {
      const items: ScreenshotComparisonItem[] = [];
      for (const child of node.children ?? []) {
        if (child.type === 'component') {
          items.push({
            fixtureId: child.id.value,
            name: child.name,
            path: `${pathPrefix} / ${child.name}`,
          });
        }
      }
      if (items.length > 0) {
        return [{
          type: 'screenshot-comparison',
          id: node.id.value,
          name: node.name,
          path: pathPrefix,
          items,
        }];
      }
      return [];
    }

    const result: PreviewRow[] = [];
    for (const child of node.children ?? []) {
      const childPath = pathPrefix ? `${pathPrefix} / ${child.name}` : child.name;
      result.push(...this._collectDiffRows(child, childPath));
    }
    return result;
  }

  private _collectReportRows(node: ExplorerTreeNode, pathPrefix: string): PreviewRow[] {
    const reportState = this.props.reportModel!.state.get();
    if (reportState.status !== 'ready') return [];
    const fixtures = reportState.report.fixtures;

    if (node.type === 'component') {
      const entry = fixtures[node.id.value];
      if (!entry) return [];
      return [{
        type: 'report-comparison',
        id: node.id.value,
        name: node.name,
        path: pathPrefix,
        items: [{
          fixtureId: node.id.value,
          name: node.name,
          path: pathPrefix,
          entry,
        }],
      }];
    }

    const result: PreviewRow[] = [];
    for (const child of node.children ?? []) {
      const childPath = pathPrefix ? `${pathPrefix} / ${child.name}` : child.name;
      result.push(...this._collectReportRows(child, childPath));
    }
    return result;
  }
}
